import { TimestampField } from '@prismicio/client';
import { format, isAfter, isBefore, parse } from 'date-fns';
import sortBy from 'lodash/sortBy';

import { TEMPLATE_BASE_PATH } from '@/constants';
import { getDistance, initializeApollo, IStoreMetadata } from '@/data';
import {
  Component,
  Geoloc,
  PromoCarouselDocumentDataCarouselItemsItemWithID,
  ScheduleWorkingHours,
  SubCat,
  TreezSpecials,
  TreezStore,
  Variant,
  WeekDay,
} from '@/types';

import { stringToSlug } from './utils';
import { toZonedDate } from './toZonedDate';

import data from '@/public/ssr-cache.json';

const client = initializeApollo();

const mapStoreToLocation = ({
  name,
  geoloc: { lat, lng },
  google_map_url,
  photo,
  url_slug,
}): {
  label: string;
  lat: number;
  lng: number;
  title: string;
  google_map_url?: string;
  photo?: string;
  url_slug: string;
} => ({
  label: name,
  lat,
  lng,
  title: name,
  google_map_url,
  photo,
  url_slug,
});

const compareDistances = (firstStore, secondStore) => {
  return firstStore.distance - secondStore.distance;
};

const mapStoreToDistance =
  (currentLocation: Geoloc) => async (store: TreezStore) => {
    const metricSystem = process.env.NEXT_PUBLIC_DISTANCE_MEASUREMENT_UNIT;
    const { lat, lng } = mapStoreToLocation(store);

    const { data, error } = await getDistance(client, {
      variables: {
        from: { latitude: lat, longitude: lng },
        to: {
          latitude: currentLocation.lat,
          longitude: currentLocation.lng,
        },
      },
    });

    if (error) {
      // eslint-disable-next-line no-console
      console.error('getDistance', error);
      return { ...store };
    }

    const currentDimension = data.find(
      d => d.dimension.toUpperCase() === metricSystem?.toUpperCase(),
    );

    if (!currentDimension) {
      return { ...store };
    }

    return { ...store, distance: currentDimension.value };
  };

// eslint-disable-next-line no-unused-vars
const checkIfStoreIsOpen = (openingTime: string, closingTime: string) => {
  return true;
};

const getHoursAndMinutes = (time: string | null) => {
  if (!time) {
    return {
      hours: 10,
      minutes: 10,
    };
  }

  const [stringHours, stringMinutes] = time.split(':');
  return {
    hours: parseInt(stringHours, 10),
    minutes: parseInt(stringMinutes, 10),
  };
};

const transformMilitaryTime = (militaryFormatTime: string): string => {
  if (!militaryFormatTime) {
    return '';
  }

  const dateObject = parse(militaryFormatTime, 'HH:mm', new Date());

  return format(dateObject, 'h:mm aa');
};

// eslint-disable-next-line no-unused-vars

const findCategoryForSubtype = (subCat: SubCat, subtype: string) => {
  for (const [category, subtypes] of Object.entries(subCat)) {
    if (subtypes.includes(subtype)) {
      return stringToSlug(category);
    }
  }
  return '';
};

const getLabValue = (v?: string): Component => {
  let percentage = 0;
  let amount = 0;

  if (!v) {
    return { percentage, amount };
  }

  if (v.includes('%')) {
    percentage = parseFloat(v);
  }

  if (v?.toLowerCase()?.includes('mg')) {
    amount = parseFloat(v);
  }

  return { percentage, amount };
};

const getStockItem = (stockList, type, subType) => {
  if (!stockList?.length) return null;

  const formattedSub = subType?.toLowerCase()?.trim();
  const formattedType = type?.toLowerCase()?.trim();

  const getStockMatch = (searchName: string) => {
    if (!searchName) return null;

    return (
      stockList?.find(
        stock => stock?.name?.toLowerCase().trim() === searchName,
      ) || null
    );
  };

  //First search for the format 'type:subtype'
  if (formattedType && formattedSub) {
    const param = `${formattedType}:${formattedSub}`;
    const subtypeMatch = getStockMatch(param);

    if (subtypeMatch) return subtypeMatch;
  }

  // Then search by type only
  if (formattedType) {
    const typeMatch = getStockMatch(formattedType);
    if (typeMatch) return typeMatch;
  }

  return null;
};

const formatWeightLabel = (type: string): string =>
  ({
    gram: '1g',
    half_gram: '0.5g',
    two_gram: '2g',
    ounce: '28g',
    half_ounce: '14g',
    quarter_ounce: '7g',
    eighth_ounce: '3.5g',
    each: 'each',
    'Not provided': 'Not provided',
  }[type] ?? generateLabel(type));

const generateLabel = (type: string) => {
  if (!type) return '';

  const [amount, unit] = type.split('_');

  if (unit === 'grams') return `${amount}g`;
  if (unit === 'milliliters') return `${amount} ml`;

  return type.replace('_', ' ');
};

const getStoresFilter = () => {
  const storeIds = process?.env?.NEXT_PUBLIC_JANE_STORES_IDS?.split(',');
  return storeIds?.map(id => `objectID:${id}`).join(' OR ');
};

const getInitialStoresFilter = () => {
  const storeIds = process?.env?.NEXT_PUBLIC_JANE_STORES_IDS?.split(',');
  return `(${storeIds?.map(id => `store_id:${id}`).join(' OR ')})`;
};

const getReviewsFeelingsCount = (reviews: any[]) =>
  Object.entries(
    reviews.reduce(
      (acc, current) => {
        const temp = acc;
        current.feelings.forEach(feeling => {
          temp[feeling] = (temp[feeling] ?? 0) + 1;
        });
        return temp;
      },
      { Blissful: 0, Creative: 0, Energetic: 0 },
    ),
  );

const getReviewsActivitiesCount = (reviews: any[]) =>
  Object.entries(
    reviews.reduce(
      (acc, current) => {
        const temp = acc;
        current.activities.forEach(activity => {
          temp[activity] = (temp[activity] ?? 0) + 1;
        });
        return temp;
      },
      {
        'Ease my mind': 0,
        'Get Active': 0,
        'Stimulate my mind': 0,
      },
    ),
  );

const feelingsIconsNameMap = {
  blissful: 'blissful',
  creative: 'creative',
  energetic: 'energetic',
  hungry: 'hungry',
  not_high: 'sentiment_satisfied',
  pain_free: 'pain_free',
  relaxed: 'relaxed',
  sleepy: 'sleepy',
};

const reviewsIconsNameMap = {
  'Ease my mind': 'ease_mind',
  'Get Active': 'vital_signs',
  'Get intimate': 'intimate',
  'Get relief': 'relief',
  'Get some sleep': 'some_sleep',
  'Hang with friends': 'hang_with_friends',
  'Stimulate my mind': 'light_bulb',
};

const getFeelingIconName = (name: string) => {
  const transformedName = name.toLowerCase().replace(/ /g, '_');
  return feelingsIconsNameMap[transformedName] ?? 'cannabis';
};

const getActivityIconName = (name: string) =>
  reviewsIconsNameMap[name] ?? 'cannabis';

const generateQueryParams = (queryParams: {
  [key: string]: string | number | boolean;
}) => {
  return Object.entries(queryParams)
    .reduce((params, [key, value]) => {
      return value ? [...params, `${key}=${value}`] : params;
    }, [])
    .join('&');
};

const getProductItemLocId = (storeId: string, weightVariant: Variant) => {
  const treezIds = new Map(
    process?.env?.NEXT_PUBLIC_TREEZ_ID_PER_STORE &&
      JSON.parse(process?.env?.NEXT_PUBLIC_TREEZ_ID_PER_STORE),
  );
  const currentTreezId = treezIds.get(storeId);

  if (!currentTreezId) {
    return `${weightVariant?.pos_id}`;
  }

  return `${currentTreezId}|${weightVariant?.pos_id}`;
};

const isBogoBetweenTheDateRange = (
  startDate: Date,
  endDate: Date | undefined,
): { currentDate: Date; isActive: boolean } => {
  const currentDate = new Date().toLocaleString('en-US', {
    hourCycle: 'h23',
    timeZone: 'America/Los_Angeles', // TODO get the info from the GC store
  });
  const formatCurrentDate = new Date(currentDate);

  const inRange = endDate ? isBefore(formatCurrentDate, endDate) : true;

  const isActive = isAfter(formatCurrentDate, startDate);

  return {
    currentDate: formatCurrentDate,
    isActive: inRange && isActive,
  };
};

const dataInDayWeek = (
  startDate: Date,
  endDate: Date | undefined,
  activeDays?: { repeatOn: { [key: string]: boolean }[] },
) => {
  const { currentDate, isActive } = isBogoBetweenTheDateRange(
    startDate,
    endDate,
  );

  if (!isActive) {
    return false;
  }

  if (!activeDays) {
    const today = format(currentDate, 'eeee');
    const start = format(startDate, 'eeee');

    return today === start;
  }

  const dayWeek = format(currentDate, 'eeee').toUpperCase().slice(0, 3);
  return activeDays.repeatOn[dayWeek];
};

const dateInRangeBy = (
  startDate: Date,
  endDate: Date | undefined,
  by: 'ANNUAL' | 'MOUNTH_DAY' | 'WEEK_DAY',
) => {
  const { currentDate, isActive } = isBogoBetweenTheDateRange(
    startDate,
    endDate,
  );

  if (!isActive) {
    return false;
  }

  if (by === 'WEEK_DAY') {
    const currentWeekDay = currentDate.getDay();
    return currentWeekDay != 0 && currentWeekDay != 6; // 0 and 6 and Sat and Sunday
  }

  // get info about Day
  const currentDay = currentDate.getUTCDate();
  const initDay = startDate.getUTCDate();

  // get info about Hours
  const currentTime = `${currentDate.getHours()}:${currentDate.getMinutes()}`;
  const startTime = `${startDate.getHours()}:${startDate.getMinutes()}`;

  if (by === 'MOUNTH_DAY') {
    return currentDay === initDay && currentTime >= startTime;
  }

  // get indo about Mounth
  const currentMounth = currentDate.getUTCMonth();
  const initMount = startDate.getUTCMonth();
  return (
    currentMounth === initMount &&
    currentDay === initDay &&
    currentTime >= startTime
  );
};

const dateInRange = (startDate: Date, endDate: Date | undefined) => {
  const { isActive } = isBogoBetweenTheDateRange(startDate, endDate);
  return isActive;
};

// Prioritizes adminWorkingHours (gc) > configWorkingHours (treez). Both use ScheduleWorkingHours format.
export const getActiveWorkingHours = ({
  adminWorkingHours,
  configWorkingHours,
}: {
  adminWorkingHours?: ScheduleWorkingHours[] | null;
  configWorkingHours?: ScheduleWorkingHours[] | null;
}): ScheduleWorkingHours[] => {
  const DAY_ORDER: Record<WeekDay, number> = {
    [WeekDay.Monday]: 1,
    [WeekDay.Tuesday]: 2,
    [WeekDay.Wednesday]: 3,
    [WeekDay.Thursday]: 4,
    [WeekDay.Friday]: 5,
    [WeekDay.Saturday]: 6,
    [WeekDay.Sunday]: 7,
  };

  const sortWorkingHoursByDay = (
    workingHours: ScheduleWorkingHours[],
  ): ScheduleWorkingHours[] =>
    sortBy(
      workingHours,
      day => DAY_ORDER[day.day_of_the_week as WeekDay] ?? Infinity,
    );

  if (adminWorkingHours && adminWorkingHours.length > 0) {
    const hasOpenDay = adminWorkingHours.some(day => day.start && day.end);
    if (!hasOpenDay) return [];

    const filtered = adminWorkingHours.filter(
      Boolean,
    ) as ScheduleWorkingHours[];
    return sortWorkingHoursByDay(filtered);
  }

  if (configWorkingHours && configWorkingHours.length > 0) {
    const hasOpenDay = configWorkingHours.some(day => day.start && day.end);
    if (!hasOpenDay) return [];

    const filtered = configWorkingHours.filter(
      Boolean,
    ) as ScheduleWorkingHours[];
    return sortWorkingHoursByDay(filtered);
  }

  return [];
};

const isCurrentDateInRange = (
  start: string | TimestampField,
  end: string | TimestampField,
  timeZone?: string,
) => {
  const currentDate = toZonedDate({ timeZone });

  const startDate = start ? toZonedDate({ date: start, timeZone }) : null;
  const endDate = end ? toZonedDate({ date: end, timeZone }) : null;

  const inRange = endDate ? currentDate <= endDate : true;
  const isActive = startDate ? currentDate >= startDate : true;

  return inRange && isActive;
};

const convertHexadecimalFormatToRGB = (hex: string) => {
  const hexParse = hex.replace(/^#/, '');

  const r = parseInt(hexParse.substring(0, 2), 16);
  const g = parseInt(hexParse.substring(2, 4), 16);
  const b = parseInt(hexParse.substring(4, 6), 16);

  return { r, g, b };
};

const getScaleColor = (color: string) => {
  const rgb = convertHexadecimalFormatToRGB(color);

  // Calculate luminance
  const luminance = 0.2126 * rgb?.r + 0.7152 * rgb?.g + 0.0722 * rgb?.b;

  const grayscaleAmount = Math.round(((255 - luminance) / 255) * 100);

  return grayscaleAmount;
};

const calculateDiscountPercent = (value: number, total: number) => {
  return 100 - Math.floor((value / total) * 100);
};

const getPreCartTreezDiscounts = (specials: TreezSpecials[]) =>
  specials?.filter(s => !s.cart && s.showEcommerce !== false);

const getSpecialLink = (
  s: TreezSpecials | PromoCarouselDocumentDataCarouselItemsItemWithID,
) =>
  `${TEMPLATE_BASE_PATH.SPECIALS}${stringToSlug(
    s?.title ??
      (s as PromoCarouselDocumentDataCarouselItemsItemWithID)?.pos_title_name,
  )}?special_id=${s?.id}`;

const getExternalStores = () => {
  // this external store config is to support a use case for Airfield store
  // so they want to be able to select the store on the store selector
  // component and redirect customers to respective cross-domain
  // store
  const s = process?.env?.NEXT_PUBLIC_EXTERNAL_STORES;
  if (!s) {
    return [];
  }

  return JSON.parse(s);
};

const getTreezStores = () => (data as unknown as IStoreMetadata)?.treezStores;

const getDefaultStore = () => {
  const treezStores = getTreezStores();

  return (
    treezStores?.find(
      ({ short_name }) =>
        short_name === process.env.NEXT_PUBLIC_TREEZ_DEFAULT_STORE_NAME,
    ) ?? treezStores?.[0]
  );
};

export {
  calculateDiscountPercent,
  checkIfStoreIsOpen,
  compareDistances,
  convertHexadecimalFormatToRGB,
  dataInDayWeek,
  dateInRange,
  dateInRangeBy,
  findCategoryForSubtype,
  formatWeightLabel,
  generateQueryParams,
  getActivityIconName,
  getExternalStores,
  getFeelingIconName,
  getHoursAndMinutes,
  getInitialStoresFilter,
  getPreCartTreezDiscounts,
  getProductItemLocId,
  getReviewsActivitiesCount,
  getReviewsFeelingsCount,
  getScaleColor,
  getSpecialLink,
  getStockItem,
  getStoresFilter,
  isCurrentDateInRange,
  mapStoreToDistance,
  mapStoreToLocation,
  transformMilitaryTime,
  getTreezStores,
  getDefaultStore,
  getLabValue,
};
